import { Switch } from '../../../../components';
import { QuestionView } from '../../../logbook/views/QuestionView';
import {
    Accordion,
    AudioPlayer,
    BulletList,
    Card,
    Exercise,
    GhostButton,
    LabelBody,
    LabelBodyHeadline,
    LabelBodySmall,
    LabelDisplay1,
    LabelDisplay2,
    ProgressBar,
    SecondaryButton,
    Spacer,
    SwirlyBox,
    Textarea,
} from '../../components';
import { ExerciseData, ExerciseViewProps } from '../../exercises';
import { ExerciseDto, ExerciseStatus } from '../../interfaces';
import {
    IllustrationPenPaper,
    Module7Exercise1Illustration5,
} from '../../icons';
import { normalizeInput, useSyncedState } from '../../useSyncedState';
import React from 'react';
import classNames from 'classnames';
import { FeedbackText, Slider } from '../../components/Slider';

const physicalEnergyKey = 'physicalEnergy';
const movementCreatesConnectionKey = 'movementCreatesConnection';
const movementDescriptionKey = 'movementDescription';

export const exerciseData311: ExerciseData = {
    ExerciseView: Module3Exercise1Part1,
    dataKeys: [
        movementCreatesConnectionKey,
        physicalEnergyKey,
        movementDescriptionKey,
    ],
    questions: [
        {
            Answers: (props) => (
                <QuestionView
                    {...props}
                    title="Hvordan er din fysiske energi?"
                    description="1 = kunne være bedre og 4 = meget god"
                    answerTitle="Din score"
                />
            ),
            dataKeys: [physicalEnergyKey],
        },
        {
            Answers: (props) => {
                const energy = Number(
                    (props.exercise.Data?.[physicalEnergyKey] as string) ?? 0,
                );
                const description =
                    energy < 3
                        ? 'Er der noget bevægelse, som jeg kunne have lyst til at gøre mere af?'
                        : 'Hvad gør jeg allerede af bevægelse som fungerer?';
                return (
                    <QuestionView
                        {...props}
                        title="Bevægelse skaber forbindelse"
                        description={description}
                    />
                );
            },
            dataKeys: [movementCreatesConnectionKey],
        },
    ],
};

const numberOfPreviousPages = 2;

function Module3Exercise1Part1({ exercise, onContinue }: ExerciseViewProps) {
    return (
        <Exercise title="Fysisk energi">
            <ProgressBar
                current={exercise.CurrentPage + numberOfPreviousPages}
                total={11}
            />
            <Switch
                cases={{
                    1: (
                        <Page1
                            sliderValue={
                                exercise.Data[physicalEnergyKey] != null
                                    ? Number(exercise.Data[physicalEnergyKey])
                                    : null
                            }
                            onFinish={() => {
                                onContinue({
                                    status: ExerciseStatus.Completed,
                                    goToNext: false,
                                });
                            }}
                            onContinue={(sliderValue) => {
                                onContinue({
                                    status: ExerciseStatus.InProgress,
                                    pageNumber: 2,
                                    data: {
                                        [physicalEnergyKey]: normalizeInput(
                                            sliderValue.toString(),
                                        ),
                                    },
                                });
                            }}
                        />
                    ),
                    2: (
                        <Page2
                            sliderValue={
                                exercise.Data[physicalEnergyKey] != null
                                    ? Number(exercise.Data[physicalEnergyKey])
                                    : null
                            }
                            exercise={exercise}
                            text={
                                exercise.Data[movementCreatesConnectionKey] ??
                                ''
                            }
                            onContinue={(text) =>
                                onContinue({
                                    status: ExerciseStatus.Completed,
                                    goToNext: '3.1.2',
                                    data: {
                                        [movementCreatesConnectionKey]:
                                            normalizeInput(text),
                                    },
                                })
                            }
                        />
                    ),
                }}
                value={exercise.CurrentPage}
                default={1}
            />
        </Exercise>
    );
}

const feedbackTexts: { [key: number]: FeedbackText } = {
    1: {
        title: 'Din fysiske energi kunne være bedre',
        text: 'Det tyder på, at fundamentet måske ikke er så stærkt, som det kunne være. Det er helt ok. Det vigtigste er, at du er bevidst om det.',
    },
    2: {
        title: 'Din fysiske energi er ok',
        text: 'Din score tyder på, at du har perioder med energi, men måske også dage hvor overskuddet mangler. Du kan overveje, hvilke af de 4 områder ( søvn, mad, motion og vejrtrækning), der føles mest udfordrende lige nu? og så starte med at ændre en lille ting.',
    },
    3: {
        title: 'Din fysiske energi er god',
        text: 'Du har formentlig allerede gode vaner, indenfor de fleste af de fire områder (søvn, mad, motion og vejrtrækning.)  Du kan tænke over, hvad der bidrager mest til dit energiniveau, så du kan holde fast i det - selv i travle perioder.',
    },
    4: {
        title: 'Din fysiske energi er meget god',
        text: 'Du prioriterer sandsynligvis din fysiske sundhed og energi. Hvilke vaner eller rutiner har du mon, som er gode for dig? Husk, at med et højt energiniveau, er det stadig vigtigt at lytte til din krops signaler og restituere, når du har brug for det.',
    },
};
type Page1Props = {
    onContinue: (sliderValue: number) => void;
    onFinish: () => void;
    sliderValue: number | null;
};
function Page1({
    onContinue,
    onFinish,
    sliderValue: _sliderValue,
}: Page1Props) {
    const [sliderValue, setSliderValue] = useSyncedState<number | null>(
        _sliderValue,
    );

    return (
        <>
            <LabelDisplay2>Vurder din fysiske energi</LabelDisplay2>
            <LabelBody className="my-10">
                Fysisk energi er fundamentet for at være til. Her er særligt
                fire ting som er vigtige:
            </LabelBody>
            <LabelBodyHeadline>
                <BulletList
                    items={['Søvn', 'Mad', 'Motion', 'Vejrtrækning']}
                ></BulletList>
            </LabelBodyHeadline>
            <LabelBody className="mt-20">
                Ved at fokusere på disse områder, kan du skabe et endnu stærkere
                fundament for overskud i hverdagen.
            </LabelBody>
            <LabelBody className="mt-20">
                Tænk over din hverdag, som den ser ud i dag. Måske får du
                bevæget dig, men sover dårligt. Det er helt ok at din score er
                et nogenlunde gennemsnit for alle fire parametre.{' '}
            </LabelBody>
            <Slider
                className="mb-30 mt-20"
                title="Hvordan er din fysiske energi?"
                description="1 = kunne være bedre og 4 = meget god"
                value={sliderValue}
                setValue={setSliderValue}
                feedbackTexts={sliderValue ? feedbackTexts[sliderValue] : null}
                backgroundImage={'/images/slider-background-1.svg'}
                min={0}
                hideZeroLabel={true}
            />
            <Spacer />
            <SecondaryButton
                disabled={!sliderValue}
                className="mb-20"
                onClick={() => onContinue(sliderValue)}
            >
                Fortsæt til tippet
            </SecondaryButton>
            <GhostButton onClick={onFinish}>Vender tilbage senere</GhostButton>
        </>
    );
}

type Page2Props = {
    exercise: ExerciseDto;
    onContinue: (text: string) => void;
    sliderValue: number;
    text: string;
};
function Page2({ exercise, onContinue, text: _text, sliderValue }: Page2Props) {
    const [openAccordion1, setOpenAccordion1] = React.useState(false);
    const [openAccordion2, setOpenAccordion2] = React.useState(false);
    const [openAccordion3, setOpenAccordion3] = React.useState(false);
    const [openAccordion4, setOpenAccordion4] = React.useState(false);
    const [text, setText] = useSyncedState(_text);
    const isEnergyLevelLow = sliderValue < 3;

    return (
        <>
            <LabelDisplay2>Sæt dig selv op til en god nattesøvn</LabelDisplay2>
            {isEnergyLevelLow ? (
                <>
                    <LabelBody className="my-10 mb-20">
                        Det ser ud til, at du med fordel kunne arbejde med din
                        fysiske energi. Her er søvn et godt sted at starte,
                        fordi den har stor betydning for, hvordan vi har det
                        fysisk.
                    </LabelBody>
                    <Module7Exercise1Illustration5 />
                    <LabelBody className="mt-20">
                        Ja, vi ved det godt. Du har sikkert hørt det før. Vi
                        gentager det, fordi det simpelthen er så vigtigt, når
                        det gælder energi og overskud i hverdagen. Hvis du har
                        brug for hjælp til at falde til ro, så kan du bruge de
                        tre lydfiler.
                    </LabelBody>
                </>
            ) : (
                <>
                    <LabelBody className="my-10 mb-20">
                        Din høje score tyder på, at du allerede har godt styr på
                        din fysiske energi. Når vi alligevel fremhæver søvn, så
                        er det fordi vi ved, at den er det vigtigste for vores
                        fysiske energi.
                    </LabelBody>
                    <Module7Exercise1Illustration5 />
                    <LabelBody className="mt-20">
                        Du kender sikkert rådene, men hvis du er nysgerrig eller
                        vil have genopfrisket lidt om søvn, så har vi samlet
                        fire anbefalinger til gode søvnvaner. Du kan også bruge
                        lydfilerne, hvis det en dag alligevel er lidt svært at
                        falde i søvn.
                    </LabelBody>
                </>
            )}
            <AudioPlayer
                exercise={exercise}
                src="https://static.hejdoktor.dk/falcksundtsind/Lydfil-fald-i-soevn.mp3"
                duration="00:11:17"
                type="audio/mpeg"
                className="mt-30"
            >
                <LabelBodySmall>Fald til ro</LabelBodySmall>
                <LabelBodyHeadline>Fald i søvn</LabelBodyHeadline>
            </AudioPlayer>
            <AudioPlayer
                exercise={exercise}
                src="https://static.hejdoktor.dk/falcksundtsind/Lydfil-kropsscanning.mp3"
                duration="00:11:28"
                type="audio/mpeg"
                className="mt-30"
            >
                <LabelBodySmall>Fald til ro</LabelBodySmall>
                <LabelBodyHeadline>Kropsscanning</LabelBodyHeadline>
            </AudioPlayer>
            <AudioPlayer
                exercise={exercise}
                src="https://static.hejdoktor.dk/falcksundtsind/Lydfil-regnvand.mp3"
                duration="00:30:00"
                type="audio/mpeg"
                className="my-30"
            >
                <LabelBodySmall>Beroligende lyde</LabelBodySmall>
                <LabelBodyHeadline>Regnvejr</LabelBodyHeadline>
            </AudioPlayer>
            <LabelDisplay1>Gode råd om søvn</LabelDisplay1>
            <Accordion
                class={classNames(
                    'pt-20',
                    openAccordion1
                        ? 'pb-10'
                        : 'border-b border-darker-grey pb-20',
                )}
                isOpen={openAccordion1}
                onChangeIsOpen={setOpenAccordion1}
                label={
                    <LabelBodyHeadline color="text-dark-red">
                        Mindset omkring søvn
                    </LabelBodyHeadline>
                }
            >
                <LabelBody className="border-b border-darker-grey pb-20">
                    Forsøg at gøre søvnen til noget, du ser frem til og kan
                    glæde dig til, frem for noget irriterende eller ubehageligt.
                    Du kan forsøge at skabe en rutine omkring sengetid, kan vi
                    træne vores hjerne til at forbinde sengen med ro og hvile.
                </LabelBody>
            </Accordion>
            <Accordion
                class={classNames(
                    'pt-20',
                    openAccordion2
                        ? 'pb-10'
                        : 'border-b border-darker-grey pb-20',
                )}
                isOpen={openAccordion2}
                onChangeIsOpen={setOpenAccordion2}
                label={
                    <LabelBodyHeadline color="text-dark-red">
                        Mængden af søvn
                    </LabelBodyHeadline>
                }
            >
                <LabelBody>
                    Det er vigtigt, at vi får sovet nok. Det styrker både din
                    krop og dit sind, da hjernen får en pause og genopladning
                    fra dagens indtryk. Det kan være hjælpsomt, at oprethold en
                    regelmæssig døgnrytme, hvor du står op og går i seng på et
                    fast tidspunkt.
                </LabelBody>
                <LabelBody className="mt-10 border-b border-darker-grey pb-20">
                    Tip: hvis du er kvinde, så kan dit søvnbehov ændre sig alt
                    efter cyklus. Mange har brug for mere søvn og hvile, når de
                    har menstruation.
                </LabelBody>
            </Accordion>
            <Accordion
                class={classNames(
                    'pt-20',
                    openAccordion3
                        ? 'pb-10'
                        : 'border-b border-darker-grey pb-20',
                )}
                isOpen={openAccordion3}
                onChangeIsOpen={setOpenAccordion3}
                label={
                    <LabelBodyHeadline color="text-dark-red">
                        Miljø omkring søvn
                    </LabelBodyHeadline>
                }
            >
                <LabelBody className="border-b border-darker-grey pb-20">
                    Det er vigtigt, at dit søvnmiljø ikke er fyldt med
                    forstyrrelser. En idé kan være at bruge ørepropper eller
                    sovemaske hvis du er generet af lyd og lys. Blåt lys fra
                    skærme inden sengetid ved vi besværliggør at falde i søvn,
                    så forsøg ikke at have mobilen med i seng, eller skru helt
                    ned for dens lys.
                </LabelBody>
            </Accordion>
            <Accordion
                class={classNames(
                    'pt-20',
                    openAccordion4
                        ? 'pb-10'
                        : 'border-b border-darker-grey pb-20',
                )}
                isOpen={openAccordion4}
                onChangeIsOpen={setOpenAccordion4}
                label={
                    <LabelBodyHeadline color="text-dark-red">
                        Fald ned før du falder i søvn
                    </LabelBodyHeadline>
                }
            >
                <LabelBody className="border-b border-darker-grey pb-20">
                    Giv dig selv tid til at falde ned og til at slappe af i
                    mindst en time før du går i seng. Aktiver dit indre
                    bremsesystem ved at slappe af med noget behageligt og
                    beroligende, fx stille musik, læsning, et varmt bad eller du
                    kan lytte til de to lydfiler her på siden. Det kan også
                    hjælpe at dæmpe belysningen sidst på aftenen.
                </LabelBody>
            </Accordion>
            <LabelDisplay2 className="mt-20">
                Bevægelse skaber forbindelse
            </LabelDisplay2>
            <LabelBody className="my-10">
                {isEnergyLevelLow
                    ? 'Bevægelse er rigtig godt for at skabe og vedligeholde fysisk energi.'
                    : 'Måske oplever du allerede at bevægelse gør noget godt for dig.'}{' '}
                For når vi bruger fysisk energi, så skaber vi også fysisk
                energi. Derfor kan du heller ikke hvile dig til mere energi. Du
                kan hvile dig for at lade op og restituere, men hvis du vil have
                mere energi, så skal du også forbruge.
            </LabelBody>
            <LabelBody>
                Når du er aktiv frigiver kroppen endorfiner, som gør dig gladere
                og mere energisk. Bevægelse kan også hjælpe dig med at sove
                bedre og være en måde at dyrke et fællesskab på. Alt sammen
                noget, der gør hverdagen lettere og sjovere.
            </LabelBody>
            <Card className="my-30">
                <IllustrationPenPaper />
                <LabelDisplay1 color="text-dark-red" className="mb-10 mt-20">
                    {isEnergyLevelLow ? 'Get moving' : 'Keep moving'}
                </LabelDisplay1>
                <LabelBody>
                    {isEnergyLevelLow
                        ? 'Er der noget bevægelse, som jeg kunne have lyst til at gøre mere af?'
                        : 'Hvad gør jeg allerede af bevægelse som fungerer?'}
                </LabelBody>
                <Textarea
                    placeholder="Skriv her..."
                    text={text}
                    onChangeText={setText}
                />
                <LabelBody className="mt-10">
                    Dit svar bliver gemt i logbogen, når du fortsætter.
                </LabelBody>
            </Card>
            <SwirlyBox>Vil du se på din følelsesmæssige energi?</SwirlyBox>
            <Spacer />
            <SecondaryButton className="mt-30" onClick={() => onContinue(text)}>
                Fortsæt
            </SecondaryButton>
        </>
    );
}
